<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import { useCurrency } from '@/composables/useCurrency'
import UiStatusPill from '@/Components/UI/UiStatusPill.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import DamagePhotos from './DamagePhotos.vue'

const { t } = useI18n()
const { formatMoney, formatDate } = useCurrency()

const props = defineProps({
  damage:    { type: Object, required: true },
  canManage: { type: Boolean, default: false },
  canDelete: { type: Boolean, default: false },
})

const emit = defineEmits(['markRepaired', 'edit', 'delete'])

const severityTone = computed(() => ({
  minor: 'muted', moderate: 'warn', severe: 'danger'
}[props.damage.severity] || 'muted'))

const statusTone = computed(() => ({
  open: 'warn', repaired: 'ok'
}[props.damage.status] || 'muted'))
</script>

<template>
  <div class="border-t border-[color:var(--line)] py-3 first:border-t-0 grid gap-2" style="grid-template-columns: 1fr auto;">
    <div class="space-y-1.5 min-w-0">
      <div class="flex items-center gap-2 flex-wrap">
        <UiStatusPill :tone="severityTone" :label="t(`damage.severity.${damage.severity}`)" />
        <UiStatusPill :tone="statusTone" :label="t(`damage.status.${damage.status}`)" />
        <span class="text-xs text-[color:var(--muted)] tabular-nums">{{ formatDate(damage.occurred_on) }}</span>
        <Link v-if="damage.rental_href" :href="damage.rental_href" class="text-xs text-[color:var(--accent)] hover:underline">
          Rental #{{ damage.rental_id }}
        </Link>
      </div>
      <p class="text-sm text-[color:var(--ink)]">{{ damage.description }}</p>
      <p v-if="damage.repair_notes" class="text-xs text-[color:var(--inkSoft)]">
        <span class="text-[color:var(--muted)]">{{ t('damage.fields.repairNotes') }}:</span> {{ damage.repair_notes }}
      </p>
      <DamagePhotos v-if="damage.photo_urls?.length" :urls="damage.photo_urls" />
    </div>
    <div class="flex flex-col items-end gap-1 text-xs tabular-nums">
      <span class="fw-value text-[color:var(--ink)]">{{ formatMoney(damage.repair_cost_cents) }}</span>
      <span v-if="damage.recovered_cents > 0" class="text-[color:var(--ok)]">
        &minus; {{ formatMoney(damage.recovered_cents) }} {{ t('damage.recovered').toLowerCase() }}
      </span>
      <span v-if="damage.net_cost_cents !== damage.repair_cost_cents" class="fw-value text-[color:var(--warn)]">
        = {{ formatMoney(damage.net_cost_cents) }}
      </span>
      <div v-if="canManage" class="flex gap-1 mt-1">
        <UiButton v-if="damage.status === 'open'" variant="ghost" size="sm" @click="emit('markRepaired', damage)">
          {{ t('damage.markRepaired') }}
        </UiButton>
        <UiButton variant="ghost" size="sm" @click="emit('edit', damage)">{{ t('damage.edit') }}</UiButton>
        <UiButton v-if="canDelete" variant="ghost" size="sm" @click="emit('delete', damage)">{{ t('damage.delete') }}</UiButton>
      </div>
    </div>
  </div>
</template>
